import AgoraRTC, {
    ClientRole,
    ConnectionDisconnectedReason,
    ConnectionState,
    IAgoraRTCClient,
    IAgoraRTCRemoteUser,
    ICameraVideoTrack,
    IMicrophoneAudioTrack,
    IRemoteAudioTrack,
    IRemoteVideoTrack,
    NetworkQuality,
    UID,
    VideoEncoderConfiguration
} from "agora-rtc-sdk-ng";
import {
    MediaStreamingHostsUpdateType,
    ParticipantRolesEnum,
    WebcastAuctionConnectData_TypeDef, WebcastAuctionHandler
} from "@nextlot/core/webcast/services/AuctionsManager";
import {TaggedLogger} from "@nextlot/core/utilities";


// https://docs.agora.io/en/Interactive%20Broadcast/start_live_web_ng?platform=Web
// https://docs.agora.io/en/Interactive%20Broadcast/API%20Reference/web_ng/v4.13.0/index.html


const AgoraLogLevels = {
    DEBUG: 0, //Output all API logs.
    INFO: 1, // Output logs of the INFO, WARNING and ERROR level.
    WARNING: 2, // Output logs of the WARNING and ERROR level.
    ERROR: 3, // Output logs of the ERROR level.
    NONE: 4, // Do not output any log.
}

AgoraRTC.setLogLevel(__DEV__ ? AgoraLogLevels.WARNING : AgoraLogLevels.INFO);
// AgoraRTC.enableLogUpload();



export enum ConnectStatusIdEnum {
    not_connected = 0,
    connecting = 3,
    connected_ok = 9,
    connect_error = -9
}


export type StreamQualitySettingsType = {
    audioProfileCode:string,
    videoProfileCode:string,
}

export type MasterRemoteUsersTypeDef = {
    audioRemoteUser?:IAgoraRTCRemoteUser|false|true,
    videoRemoteUser?:IAgoraRTCRemoteUser|false|true,
}




export const MEDIA_QUALITY_VIDEO_PROFILES_CODES_MAP:{ [key: string]: VideoEncoderConfiguration } = {
    '160_120_7': { bitrateMin: 60*1024, bitrateMax: 140*1024, frameRate: { min: 5, max: 7 }, width: 160, height: 120 },
    '160_120_15': { bitrateMin: 100*1024, bitrateMax: 200*1024, frameRate: { min: 11, max: 15 }, width: 160, height: 120 },

    '320_240_2': { bitrateMin: 100*1024, bitrateMax: 200*1024, frameRate: { min: 2, max: 3 }, width: 320, height: 240 },
    '320_240_7': { bitrateMin: 100*1024, bitrateMax: 200*1024, frameRate: { min: 5, max: 7 }, width: 320, height: 240 },
    '320_240_15': { bitrateMin: 180*1024, bitrateMax: 250*1024, frameRate: { min: 14, max: 16 }, width: 320, height: 240 },

    '640_480_2': { bitrateMin: 150*1024, bitrateMax: 250*1024, frameRate: { min: 2, max: 3 }, width: 640, height: 480 },
    '640_480_7': { bitrateMin: 350*1024, bitrateMax: 450*1024, frameRate: { min: 5, max: 8 }, width: 640, height: 480 },
    '640_480_15': { bitrateMin: 450*1024, bitrateMax: 550*1024, frameRate: { min: 14, max: 16 }, width: 640, height: 480 },
    '640_480_30': { bitrateMin: 700*1024, bitrateMax: 1000*1024, frameRate: { min: 25, max: 30 }, width: 640, height: 480 },

    '1280_720_7': { bitrateMin: 1000*1024, bitrateMax: 1200*1024, frameRate: { min: 5, max: 7 }, width: 1280, height: 720 },
    '1280_720_15': { bitrateMin: 1100*1024, bitrateMax: 1500*1024, frameRate: { min: 11, max: 15 }, width: 1280, height: 720 },
};



export const MEDIA_QUALITY_AUDIO_PROFILES_CODES_MAP = {
    'low': 'speech_low_quality',
    'med': 'speech_standard',
    'standard': 'music_standard',
    'high': 'high_quality',
};


enum AgoraMediaTypeEnum {
    video = "video",
    audio = "audio"
}

type StreamingConnectDataTypeDef = Pick<WebcastAuctionConnectData_TypeDef,
    'auctionId'|'participantId'|'participantRole'|
    'streamingAgoraAppId'|'streamingAgoraChannelName'|'streamingAgoraToken'>



/**
 * private abstract base class for Publisher and Subscriber
 */
class AbstractAgoraService {
    protected _logger:TaggedLogger;

    protected readonly agoraAppId:string;
    public readonly agoraChannelName:string;
    protected readonly agoraToken:string;
    protected readonly rtcClientRole:ClientRole;
    protected readonly rtcClient:IAgoraRTCClient;
    protected readonly webcastAuctionHandler:WebcastAuctionHandler;

    public myAgoraUid:UID;

    protected wbsAudioUid:UID;
    protected wbsVideoUid:UID;

    protected availableAudioRemoteUsers:Map<UID, IAgoraRTCRemoteUser>;
    protected availableVideoRemoteUsers:Map<UID, IAgoraRTCRemoteUser>;

    protected currentMasterRemoteUsers:MasterRemoteUsersTypeDef;
    // protected currentMasterAudioRemoteUser:IAgoraRTCRemoteUser|false|true;
    // protected currentMasterVideoRemoteUser:IAgoraRTCRemoteUser|false|true;

    private unsubscribeMediaHostUpdateListener:()=>void;
    private listener_remoteMasterStreamsChanged:(change:MasterRemoteUsersTypeDef)=>void;



    constructor(webcastAuctionHandler:WebcastAuctionHandler) {
        this._logger = TaggedLogger.get(this.constructor.name);

        this._logger.info('.constructor() ', webcastAuctionHandler);

        this.webcastAuctionHandler = webcastAuctionHandler;
        this.agoraAppId = webcastAuctionHandler.auctionConnectData.streamingAgoraAppId;
        this.agoraChannelName = webcastAuctionHandler.auctionConnectData.streamingAgoraChannelName;
        this.agoraToken = webcastAuctionHandler.auctionConnectData.streamingAgoraToken;

        this.rtcClientRole = webcastAuctionHandler.auctionConnectData.participantRole === ParticipantRolesEnum.clerk
            ? 'host'
            : 'audience';

        this.availableAudioRemoteUsers = new Map();
        this.availableVideoRemoteUsers = new Map();
        this.currentMasterRemoteUsers = {};

        this.rtcClient = AgoraRTC.createClient({ mode: "live", codec: "h264", role: this.rtcClientRole });

        (window as any)._dev_agoraRtcClient = this.rtcClient; // DEBUG
    }


    _registerListeners() {
        this.rtcClient.on('connection-state-change', (curState: ConnectionState, prevState: ConnectionState, reason?: ConnectionDisconnectedReason) => {
            this._logger.debug(`.rtcClient>>[connection-state-change]: `, prevState, '->', curState, ' reason:',reason);
        });

        this.rtcClient.on('network-quality', (stats: NetworkQuality) => {
            // this._logger.debug(`.rtcClient>>[network-quality]: `, stats);
        });

        this.rtcClient.on('exception', (event) => {
            this._logger.error(`.rtcClient>>[exception]: `, event);
        });

        this.rtcClient.on('token-privilege-will-expire', () => {
            this._logger.warn(`.rtcClient>>[token-privilege-will-expire]: `);
        });

        // this.rtcClient.on('user-joined', (user: IAgoraRTCRemoteUser) => {
        //     this._logger.debug(`.rtcClient>>[user-joined]: user:`, user);
        // });
        //
        // this.rtcClient.on('user-left', (user: IAgoraRTCRemoteUser, reason) => {
        //     this._logger.debug(`.rtcClient>>[user-left]: user:`, user, '  reason:', reason);
        // });



        this.rtcClient.on('user-published', (user: IAgoraRTCRemoteUser, mediaType) => {
            // this._logger.debug(`.rtcClient>>[user-published]: mediaType:`, mediaType, '  user:', user);
            this._handleRemoteTrackAdded(user, <AgoraMediaTypeEnum>mediaType);
        });

        this.rtcClient.on('user-unpublished', (user: IAgoraRTCRemoteUser, mediaType) => {
            // this._logger.debug(`.rtcClient>>[user-unpublished]: mediaType:`, mediaType, '  user:', user);
            this._handleRemoteTrackRemoved(user, <AgoraMediaTypeEnum>mediaType);
        });


        this.unsubscribeMediaHostUpdateListener = this.webcastAuctionHandler.mediaStreamingHandler.onMediaStreamingHostsUpdated(this._handleWbsStreamingHostUpdated);
    }


    _handleRemoteTrackAdded = (user:IAgoraRTCRemoteUser, mediaType:AgoraMediaTypeEnum) => {
        this._logger.debug('_handleRemoteTrackAdded: ', user.uid, mediaType, user);

        if (mediaType === AgoraMediaTypeEnum.audio) {
            this.availableAudioRemoteUsers.set(user.uid, user);

            // if this newly added track is the WBS designated master track, then fire the change event
            if (this.wbsAudioUid === user.uid) {
                this._fireMasterStreamsChanged({ audioRemoteUser: user });
            }
        }
        else if (mediaType === AgoraMediaTypeEnum.video) {
            this.availableVideoRemoteUsers.set(user.uid, user);

            // if this newly added track is the WBS designated master track, then fire the change event
            if (this.wbsVideoUid === user.uid) {
                this._fireMasterStreamsChanged({ videoRemoteUser: user });
            }
        }
        else throw new Error(`._handleRemoteTrackAdded: invalid mediaType: '${mediaType}'`); // should never get this error
    };



    _handleRemoteTrackRemoved = (user:IAgoraRTCRemoteUser, mediaType:AgoraMediaTypeEnum) => {
        this._logger.debug('_handleRemoteTrackRemoved: ', user.uid, mediaType, user);

        if (mediaType === AgoraMediaTypeEnum.audio) {
            this.availableAudioRemoteUsers.delete(user.uid);

            // if this removed track is the WBS designated master track, then fire the change event to tell the UI to unsubscribe+stop-play
            if (this.wbsAudioUid === user.uid) {
                this._fireMasterStreamsChanged({ audioRemoteUser: false });
            }
        }
        else if (mediaType === AgoraMediaTypeEnum.video) {
            this.availableVideoRemoteUsers.delete(user.uid);

            // if this removed track is the WBS designated master track, then fire the change event to tell the UI to unsubscribe+stop-play
            if (this.wbsVideoUid === user.uid) {
                this._fireMasterStreamsChanged({ videoRemoteUser: false });
            }
        }
        else throw new Error(`._handleRemoteTrackRemoved: invalid mediaType: '${mediaType}'`); // should never get this error
    };



    _handleWbsStreamingHostUpdated = (update:MediaStreamingHostsUpdateType) => {
        this._logger.debug('_handleWbsStreamingHostUpdated: update:', update);

        if (this.wbsAudioUid != update.wbsAudioUid) {

            this.wbsAudioUid = update.wbsAudioUid;

            let remoteUser;

            if (update.wbsAudioUid === this.myAgoraUid) {
                // we are the master RemoteUser! i.e. publisher
                remoteUser = true;
            }
            else if (update.wbsAudioUid) {
                remoteUser = this.availableAudioRemoteUsers.get(update.wbsAudioUid) || null; // ensure value is `null`, to compare below
            }
            else {
                // update.wbsAudioUid is NULL
                remoteUser = false;
            }

            // only if there was a valid change
            if (remoteUser !== null) {
                this._fireMasterStreamsChanged({audioRemoteUser: remoteUser});
            }
        }


        if (this.wbsVideoUid != update.wbsVideoUid) {

            this.wbsVideoUid = update.wbsVideoUid;

            let remoteUser;

            if (update.wbsVideoUid === this.myAgoraUid) {
                // we are the master RemoteUser! i.e. publisher
                remoteUser = true;
            }
            else if (update.wbsVideoUid) {
                remoteUser = this.availableVideoRemoteUsers.get(update.wbsVideoUid) || null; // ensure value is `null`, to compare below
            }
            else {
                // update.wbsVideoUid is NULL
                remoteUser = false;
            }

            // only if there was a valid change
            if (remoteUser !== null) {
                this._fireMasterStreamsChanged({videoRemoteUser: remoteUser});
            }
        }
    };


    _fireMasterStreamsChanged(change:MasterRemoteUsersTypeDef) {
        // this._logger.debug("_fireMasterStreamsChanged: change:", change);


        if (typeof change.audioRemoteUser !== 'undefined') {
            this.currentMasterRemoteUsers.audioRemoteUser = change.audioRemoteUser;
        }
        if (typeof change.videoRemoteUser !== 'undefined') {
            this.currentMasterRemoteUsers.videoRemoteUser = change.videoRemoteUser;
        }

        // notify listener if it exists
        this.listener_remoteMasterStreamsChanged && this.listener_remoteMasterStreamsChanged(change);
    }




    async connectAndJoinChannel():Promise<void> {
        try {
            this._registerListeners();

            this._logger.log(".connectAndJoinChannel>init SUCCESS. Setting role `"+ this.rtcClientRole +"` and joining channel: " + this.agoraChannelName);

            // await this.rtcClient.setClientRole(this.rtcClientRole);

            this.myAgoraUid = await this.rtcClient.join(this.agoraAppId, this.agoraChannelName, this.agoraToken);

            this._logger.log(".connectAndJoinChannel>join[" + this.agoraChannelName + "] SUCCESS with UID: " + this.myAgoraUid);
        }
        catch(ex) {
            this._logger.error(`.connectAndJoinChannel: FAILED channel[${this.rtcClientRole}] role[${this.rtcClientRole}]`, ex);
        }
    }


    /**
     * register change listener
     */
    onRemoteMasterStreamsChanged(listener:(change:MasterRemoteUsersTypeDef)=>any):()=>void {
        if (this.listener_remoteMasterStreamsChanged) {
            throw 'AgoraService.onRemoteMasterStreamsChanged: listener already registered';
        }

        this.listener_remoteMasterStreamsChanged = listener;

        // trigger the listener on registration, with the current values
        this.listener_remoteMasterStreamsChanged(this.currentMasterRemoteUsers);

        return () => {
            // return a listener unsubscribe function
            this.listener_remoteMasterStreamsChanged = null;
        }
    }


    destroy() {
        this._logger.debug('.destroy()');
        if (this.rtcClient) {
            this.rtcClient.leave();
        }
    }
}






export class AgoraPublisher extends AbstractAgoraService {

    public localMediaDevicesAudio:Array<MediaDeviceInfo>;
    public localMediaDevicesVideo:Array<MediaDeviceInfo>;

    public localAudioTrack:IMicrophoneAudioTrack;
    public localVideoTrack:ICameraVideoTrack;

    private isPublishActive: boolean = false;


    constructor(args) {
        super(args);
        (window as any)._dev_agoraPublisher = this;
    }




    async scanAndLoadLocalMediaDevices():Promise<AgoraPublisher> {
        [this.localMediaDevicesAudio, this.localMediaDevicesVideo] = await Promise.all([
            AgoraRTC.getMicrophones(),
            AgoraRTC.getCameras(),
        ]);
        return this;
    }


    async createLocalTracks():Promise<AgoraPublisher> {
        [this.localAudioTrack, this.localVideoTrack] = await Promise.all([
            AgoraRTC.createMicrophoneAudioTrack(),
            AgoraRTC.createCameraVideoTrack()
        ]);
        return this;
    }


    async publish() {
        this._logger.debug('.publish() localTracks: audioTrack:', !this.localAudioTrack.muted, ' videoTrack:', !this.localVideoTrack.muted, 'publishActive:', this.isPublishActive);

        if (! this.isPublishActive) {
            await this.rtcClient.publish([this.localAudioTrack, this.localVideoTrack]);
            this.isPublishActive = true;
        }

        // send WBS message for published stream video/audio
        this.webcastAuctionHandler.controller.sendStreamingHostUpdate(this.myAgoraUid,
            { hasAudio: !this.localAudioTrack.muted, hasVideo: !this.localVideoTrack.muted });
    }


    async unpublish() {
        await this.rtcClient.unpublish();
        this.isPublishActive = false;
    }

}







export class AgoraSubscriber extends AbstractAgoraService {

    public subscribedRemoteAudioTrack:IRemoteAudioTrack = null;
    public subscribedRemoteVideoTrack:IRemoteVideoTrack = null;


    constructor(args) {
        super(args);
        (window as any)._dev_agoraSubscriber = this;
    }


    registerAutoSubscribeToMasterRemoteUsers(listenerFn:(agoraSubscriber:AgoraSubscriber)=>any) {
        this._logger.debug('.registerAutoSubscribeToMasterRemoteUsers()');

        const unsubscribeChangeListener = this.onRemoteMasterStreamsChanged(async (change) => {

            this._logger.debug('.registerAutoSubscribeToMasterRemoteUsers()>onRemoteMasterStreamsChanged: ', change);

            // the newly designated RemoteUser, can be null (falsey) !
            const newAudioRemoteUser:IAgoraRTCRemoteUser = (typeof change.audioRemoteUser === 'object') && change.audioRemoteUser;
            const newAudioRemoteUserUid:UID = newAudioRemoteUser?.uid;

            // the currently subscribed RemoteUser, can also be null (falsey) !
            const subscribedAudioRemoteUserUid = this.subscribedRemoteAudioTrack?.getUserId();


            this._logger.debug('.registerAutoSubscribeToMasterRemoteUsers()>onRemoteMasterStreamsChanged: subscribedAudioRemoteUserUid', subscribedAudioRemoteUserUid);

            // if we are subscribed, and the master RemoteUser has gone away or changed, then ...
            if (subscribedAudioRemoteUserUid && (change.audioRemoteUser === false || (newAudioRemoteUserUid && subscribedAudioRemoteUserUid !== newAudioRemoteUserUid))) {
                // unsubscribe
                this.subscribedRemoteAudioTrack = null;
                this._logger.debug('.registerAutoSubscribeToMasterRemoteUsers()>onRemoteMasterStreamsChanged: ', 'UN-subscribe AUDIO -> null');
                const remoteUser = this.availableAudioRemoteUsers.get(subscribedAudioRemoteUserUid);
                if (remoteUser) {
                    this._logger.debug('.registerAutoSubscribeToMasterRemoteUsers()>onRemoteMasterStreamsChanged: ', 'UN-subscribe AUDIO', remoteUser);
                    await this.rtcClient.unsubscribe(remoteUser, AgoraMediaTypeEnum.audio);
                }
            }


            // if there's a new RemoteUser designated, and it's different then our current one ...
            if (newAudioRemoteUserUid && subscribedAudioRemoteUserUid !== newAudioRemoteUserUid) {
                // subscribe to it
                this._logger.debug('.registerAutoSubscribeToMasterRemoteUsers()>onRemoteMasterStreamsChanged: ', 'subscribe AUDIO', newAudioRemoteUser);
                this.subscribedRemoteAudioTrack = await this.rtcClient.subscribe(newAudioRemoteUser, AgoraMediaTypeEnum.audio);
            }

            // ... and again, for video

            const newVideoRemoteUser:IAgoraRTCRemoteUser = (typeof change.videoRemoteUser === 'object') && change.videoRemoteUser;
            const newVideoRemoteUserUid:UID = newVideoRemoteUser?.uid;

            const subscribedVideoRemoteUserUid = this.subscribedRemoteVideoTrack?.getUserId();

            if (subscribedVideoRemoteUserUid && (change.videoRemoteUser === false || (newVideoRemoteUserUid && subscribedVideoRemoteUserUid !== newVideoRemoteUserUid))) {
                this.subscribedRemoteVideoTrack = null;
                this._logger.debug('.registerAutoSubscribeToMasterRemoteUsers()>onRemoteMasterStreamsChanged: ', 'UN-subscribe VIDEO -> null');
                const remoteUser = this.availableVideoRemoteUsers.get(subscribedVideoRemoteUserUid);
                if (remoteUser) {
                    this._logger.debug('.registerAutoSubscribeToMasterRemoteUsers()>onRemoteMasterStreamsChanged: ', 'UN-subscribe VIDEO', remoteUser);
                    await this.rtcClient.unsubscribe(remoteUser, AgoraMediaTypeEnum.video);
                }
            }

            if (newVideoRemoteUserUid && subscribedVideoRemoteUserUid !== newVideoRemoteUserUid) {
                this._logger.debug('.registerAutoSubscribeToMasterRemoteUsers()>onRemoteMasterStreamsChanged: ', 'subscribe VIDEO', newVideoRemoteUser);
                this.subscribedRemoteVideoTrack = await this.rtcClient.subscribe(newVideoRemoteUser, AgoraMediaTypeEnum.video);
            }

            listenerFn(this);
        });


        return () => {
            unsubscribeChangeListener();
        }
    }


}

